import { useState, useMemo } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { Input } from "@/components/ui/input";
import {
  defaultNewsHeadlines,
  type NewsHeadline,
} from "@/components/sections/NewsAndHappenings";
import { Link } from "@/lib/router-compat";
import { Search, ArrowRight } from "lucide-react";

const wordpressArticles: (NewsHeadline & { excerpt?: string })[] = [
  {
    date: "OCTOBER 24, 2026",
    title: "How Long Does a TMS Treatment Last? Session & Course Timeline",
    href: "/how-long-does-a-tms-treatment-last",
    category: "TMS Therapy",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Lack of Sleep Is Compromising the Mental Health of 78% of Adults",
    href: "/lack-of-sleep-is-compromising-the-mental-health-of-78-of-adults",
    category: "Mental Health",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "New Hope for Treating Chronic Pain Without Opioids in Florida",
    href: "/new-hope-for-treating-chronic-pain-without-opioids-in-florida",
    category: "Chronic Pain",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "What is the Difference Between Bipolar 1 and Bipolar 2?",
    href: "/what-is-the-difference-between-bipolar-1-and-bipolar-2",
    category: "Bipolar Disorder",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Is It Better to See a Psychiatrist or Psychologist?",
    href: "/is-it-better-to-see-a-psychiatrist-or-psychologist",
    category: "Mental Health",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "What is TMS Therapy? Complete Guide & Clinical Insights",
    href: "/what-is-tms-therapy",
    category: "TMS Therapy",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Anxiety Nausea: Why It Happens & How to Stop It",
    href: "/anxiety-nausea-why-it-happens-how-to-stop-it",
    category: "Anxiety",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Psychotherapy Supports Mental Wellness",
    href: "/psychotherapy-supports-mental-wellness",
    category: "Therapy",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Is Chronic Pain a Disability? ADA, SSDI & Accommodations",
    href: "/is-chronic-pain-a-disability",
    category: "Chronic Pain",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Intro to Ketamine Therapy in Coral Springs, FL",
    href: "/intro-to-ketamine-therapy-coral-springs-fl",
    category: "Ketamine",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Essential Facts: Bipolar 1 vs 2 Coral Springs Guide",
    href: "/essential-facts-bipolar-1-vs-2-coral-springs",
    category: "Bipolar Disorder",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "How TMS Therapy Works in Coral Springs, FL",
    href: "/how-tms-therapy-works-coral-springs-fl",
    category: "TMS Therapy",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "How TMS Therapy Affects the Brain: Neuroscience Explained",
    href: "/how-tms-therapy-affects-the-brain",
    category: "TMS Therapy",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Essential Guide to OCD Symptoms and Care",
    href: "/essential-guide-to-ocd-symptoms-and-care",
    category: "OCD",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Ketamine vs SPRAVATO Essentials: Key Differences",
    href: "/ketamine-vs-spravato-essentials",
    category: "Ketamine",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "What's the Best Treatment for Depression? A Simple Guide",
    href: "/whats-the-best-treatment-for-depression-a-simple-guide-to-understanding-your-options",
    category: "Depression",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Still Looking for Depression Help? There Is New Hope",
    href: "/still-looking-for-depression-help",
    category: "Depression",
  },
  {
    date: "OCTOBER 24, 2026",
    title: "Why Patients Travel to Florida for SAINT® TMS",
    href: "/why-patients-travel-to-florida-for-saint-tms",
    category: "SAINT TMS",
  },
];

const allArticles: (NewsHeadline & { excerpt?: string })[] = [
  ...wordpressArticles,
  ...defaultNewsHeadlines,
];

const ArticlesPage = () => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const categories = useMemo(() => {
    const set = new Set(allArticles.map((a) => a.category || "Other"));
    return ["All", ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allArticles.filter((a) => {
      const matchesCategory =
        activeCategory === "All" || (a.category || "Other") === activeCategory;
      const matchesQuery = !q || a.title.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [query, activeCategory]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Articles & Resources | Neuro Wellness TMS"
        description="Search our educational articles and clinical resources on SAINT® TMS, depression, anxiety, treatment-resistant depression, and advanced mental health treatment options."
        canonical="/articles"
        keywords="depression articles, TMS clinical resources, SAINT TMS education, mental health articles, treatment-resistant depression"
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero */}
        <section className="relative bg-gradient-navy text-primary-foreground py-16 sm:py-24 overflow-hidden">
          <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
          <div className="container-px relative mx-auto max-w-5xl text-center space-y-4">
            <span className="inline-block rounded-md bg-accent px-6 py-2 text-sm sm:text-base font-bold text-white shadow-md uppercase tracking-wider">
              Educational Resources
            </span>
            <h1 className="font-headings text-3xl sm:text-5xl font-extrabold text-white">
              Articles &amp; Clinical Resources
            </h1>
            <p className="text-base sm:text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              Search our growing library of plain-language clinical articles on
              SAINT® TMS, treatment-resistant depression, anxiety, and advanced
              mental health treatment options.
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none pointer-events-none">
            <svg
              className="relative block w-full h-8 sm:h-14 text-background"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              fill="currentColor"
            >
              <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,40 L1200,120 L0,120 Z" />
            </svg>
          </div>
        </section>

        {/* Search + Filter */}
        <section className="py-10 sm:py-14 bg-background">
          <div className="container-px mx-auto max-w-5xl space-y-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search articles by title or topic..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-12 h-12 text-base"
                aria-label="Search articles"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    activeCategory === cat
                      ? "bg-gradient-accent text-accent-foreground shadow-glow"
                      : "bg-muted text-foreground/70 hover:bg-muted/70"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <p className="text-sm text-muted-foreground">
              {filtered.length} article{filtered.length === 1 ? "" : "s"} found
            </p>

            {/* Results */}
            {filtered.length === 0 ? (
              <div className="rounded-xl border border-border bg-card p-10 text-center">
                <p className="font-headings text-lg font-bold text-primary">
                  No articles match your search
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Try a different keyword or browse all categories.
                </p>
                <button
                  onClick={() => {
                    setQuery("");
                    setActiveCategory("All");
                  }}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-accent hover:underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="divide-y divide-border rounded-xl border border-border bg-card overflow-hidden">
                {filtered.map((item, idx) => (
                  <article
                    key={idx}
                    className="group p-5 sm:p-6 transition-colors hover:bg-muted/40"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="space-y-1.5 max-w-3xl">
                        <div className="flex items-center gap-3">
                          <time
                            dateTime={item.date}
                            className="text-xs font-bold tracking-wider uppercase text-accent"
                          >
                            {item.date}
                          </time>
                          {item.category && (
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground/50 border-l border-border pl-3">
                              {item.category}
                            </span>
                          )}
                        </div>
                        <h2 className="font-headings text-lg sm:text-xl font-bold text-primary group-hover:text-accent transition-colors leading-snug">
                          <Link to={item.href} className="focus:outline-hidden">
                            {item.title}
                          </Link>
                        </h2>
                      </div>
                      <Link
                        to={item.href}
                        className="shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-accent group-hover:text-primary transition-colors"
                        aria-label={`Read article: ${item.title}`}
                      >
                        <span>Read Article</span>
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <Consultation />
      </main>
      <Footer />
    </div>
  );
};

export default ArticlesPage;
